import { ref, computed, onMounted } from 'vue'
import { listReimbursements } from '@/services/account'
import { mapReimbursement } from '@/mappers/reimbursement.mapper'
import type { Reimbursement, ReimbursementAct, ReimbursementStatus } from '@/data/seedMember'

/**
 * The member's health reimbursements (`refund` / `care` operations).
 *
 * Read-only. Fetches on mount and owns the whole request lifecycle (loading,
 * success, error) plus the derivation of a statement — total reimbursed,
 * remaining cost, and the current-month summary — so the view stays declarative
 * and those rules live in exactly one place.
 */

/** Acte enrichi des montants dérivés. */
export interface ReimbursementActRow extends ReimbursementAct {
  /** Total remboursé sur l'acte. */
  reimbursed: number
  /** Reste à charge de l'acte. */
  remaining: number
}

/** Remboursement enrichi des montants dérivés, tel que consommé par la vue. */
export interface ReimbursementRow {
  id: string
  careDate: string
  label: string
  cost: number
  socialSecurity: number
  smerra: number
  /** Total remboursé = part sécurité sociale + part Smerra. */
  reimbursed: number
  /** Reste à charge = montant facturé − total remboursé. */
  remaining: number
  status: ReimbursementStatus
  acts: ReimbursementActRow[]
  /** Vrai dès que le décompte regroupe plusieurs actes. */
  isMultiAct: boolean
}

/** Récapitulatif affiché en tête de page (mois en cours). */
export interface ReimbursementSummary {
  reimbursed: number
  smerra: number
  socialSecurity: number
  remaining: number
}

/** Les montants sont manipulés en euros : on arrondit au centime pour éviter
 *  les artefacts de virgule flottante sur les sommes (0.1 + 0.2…). */
function round(value: number): number {
  return Math.round(value * 100) / 100
}

/** Montants dérivés d'une prise en charge. Même règle pour un acte isolé et
 *  pour le décompte qui les regroupe. Le statut, lui, vient du back-end : il
 *  connaît les sur-couvertures et les écarts par acte. */
function derive(cost: number, socialSecurity: number, smerra: number) {
  const reimbursed = round(socialSecurity + smerra)
  return { reimbursed, remaining: round(cost - reimbursed) }
}

function toActRow(act: ReimbursementAct): ReimbursementActRow {
  return { ...act, ...derive(act.cost, act.socialSecurity, act.smerra) }
}

function toRow(reimbursement: Reimbursement): ReimbursementRow {
  return {
    ...reimbursement,
    ...derive(reimbursement.cost, reimbursement.socialSecurity, reimbursement.smerra),
    acts: reimbursement.acts.map(toActRow),
    isMultiAct: reimbursement.acts.length > 1,
  }
}

/** Vrai si la date ISO tombe dans le mois calendaire courant. */
function isCurrentMonth(iso: string): boolean {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return false
  const now = new Date()
  return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth()
}

export function useReimbursements() {
  const rows = ref<ReimbursementRow[]>([])
  const loading = ref(false)
  const failed = ref(false)

  /** Relevé trié du soin le plus récent au plus ancien : `GET /operations`
   *  renvoie l'union de deux scopes, sans ordre garanti. */
  const reimbursements = computed<ReimbursementRow[]>(() =>
    [...rows.value].sort((a, b) => b.careDate.localeCompare(a.careDate)),
  )

  /** Récapitulatif du mois en cours (libellé « Sur le mois en cours »). */
  const summary = computed<ReimbursementSummary>(() => {
    const current = reimbursements.value.filter((row) => isCurrentMonth(row.careDate))
    const sum = (pick: (row: ReimbursementRow) => number) =>
      round(current.reduce((total, row) => total + pick(row), 0))
    return {
      reimbursed: sum((row) => row.reimbursed),
      smerra: sum((row) => row.smerra),
      socialSecurity: sum((row) => row.socialSecurity),
      remaining: sum((row) => row.remaining),
    }
  })

  async function load(): Promise<void> {
    if (loading.value) return
    loading.value = true
    failed.value = false
    try {
      const operations = await listReimbursements()
      rows.value = operations.map(mapReimbursement).map(toRow)
    } catch {
      failed.value = true
      rows.value = []
    } finally {
      loading.value = false
    }
  }

  onMounted(load)

  return { reimbursements, summary, loading, failed, reload: load }
}
